import React, { useState, useEffect, useCallback } from "react";
import { Button } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";

import DistrictTable from "../components/district/DistrictTable";
import DistrictForm from "../components/district/DistrictForm";

import {
  getDistricts,
  createDistrict,
  updateDistrict,
  deleteDistrict
} from "../api/services/DistrictService";

const District = () => {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  /* -------------------------------------------------------------------------- */
  /* FETCH DISTRICTS                                                            */
  /* -------------------------------------------------------------------------- */
  const fetchDistricts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getDistricts();
      setDistricts(response || []);
    } catch (err) {
      console.error("Error fetching districts:", err);
      setError("Failed to load districts");
      setDistricts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /* -------------------------------------------------------------------------- */
  /* CREATE / UPDATE HANDLER                                                     */
  /* -------------------------------------------------------------------------- */
  const handleSave = async (data) => {
    try {
      setFormLoading(true);

      if (selectedDistrict) {
        await updateDistrict(selectedDistrict._id, data);
      } else {
        await createDistrict(data);
      }

      setShowForm(false);
      setSelectedDistrict(null);
      fetchDistricts();
    } catch (err) {
      console.error("Save failed:", err);
      setError(err.message || "Failed to save district");
    } finally {
      setFormLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /* DELETE HANDLER                                                             */
  /* -------------------------------------------------------------------------- */
  const handleDelete = async (district) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${district.name}?`
    );

    if (!confirmDelete) return;

    try {
      await deleteDistrict(district._id);
      fetchDistricts();
    } catch (err) {
      console.error("Delete failed:", err);
      setError("Failed to delete district");
    }
  };

  /* -------------------------------------------------------------------------- */
  /* EDIT HANDLER                                                               */
  /* -------------------------------------------------------------------------- */
  const handleEdit = (district) => {
    setSelectedDistrict(district);
    setShowForm(true);
  };

  useEffect(() => {
    fetchDistricts();
  }, [fetchDistricts]);

  /* -------------------------------------------------------------------------- */
  /* UI STATES                                                                  */
  /* -------------------------------------------------------------------------- */
  if (loading) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-3">Loading districts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger mx-3 mt-3 d-flex justify-content-between align-items-center">
        <span>{error}</span>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={fetchDistricts}
        >
          Retry
        </Button>
      </div>
    );
  }

  /* -------------------------------------------------------------------------- */
  /* MAIN VIEW                                                                  */
  /* -------------------------------------------------------------------------- */
  return (
    <div>
      <header className="content-header d-flex justify-content-between align-items-center mb-2">
        <h1 className="mb-0">Districts</h1>

        <Button
          variant="primary"
          className="d-flex align-items-center"
          onClick={() => {
            setSelectedDistrict(null);
            setShowForm(true);
          }}
        >
          <FaPlus className="me-2" />
          Add District
        </Button>
      </header>

      <DistrictTable
        data={districts}
        pageSize={10}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* ---------------------------------------------------------------------- */}
      {/* DISTRICT FORM MODAL                                                     */}
      {/* ---------------------------------------------------------------------- */}
      <DistrictForm
        show={showForm}
        onClose={() => {
          setShowForm(false);
          setSelectedDistrict(null);
        }}
        onSubmit={handleSave}
        initialData={selectedDistrict}
        loading={formLoading}
      />
    </div>
  );
};

export default District;
